import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
    Download, Loader2, RefreshCw, Waves, ArrowUpRight, AlertTriangle,
    CheckCircle2, Clock, FileText, Play, PieChart,
} from "lucide-react";
import { toast } from "sonner";
import api, { API_BASE } from "../lib/api";
import { useAuth } from "../lib/auth";
import { ROLES } from "../lib/roles";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";

const TIPO_BADGE = {
    api_pull: "badge-gold",
    webhook_push: "badge-mock",
    sftp_scheduled: "badge-gold",
};
const ESTADO_STYLE = {
    exitoso: { badge: "badge-exito", icon: CheckCircle2 },
    parcial: { badge: "badge-gold", icon: AlertTriangle },
    fallido: { badge: "badge-error", icon: AlertTriangle },
    procesando: { badge: "badge-mock", icon: Clock },
};

function fmt(iso) {
    if (!iso) return "—";
    return iso.slice(0, 16).replace("T", " ");
}

export default function Ingestas() {
    const { hasRole } = useAuth();
    const [params, setParams] = useSearchParams();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openDetail, setOpenDetail] = useState(null); // ingesta detail

    const estado = params.get("estado") || "";
    const tipo = params.get("tipo") || "";
    const facilityIdParam = params.get("facility_id") || "";

    const canRetry = hasRole([
        ROLES.ADMINISTRADOR_FIDUCIARIO, ROLES.ADMIN_TI, ROLES.OPERACIONES,
    ]);

    const load = async () => {
        setLoading(true);
        try {
            const q = new URLSearchParams();
            if (estado) q.set("estado", estado);
            if (tipo) q.set("tipo", tipo);
            if (facilityIdParam) q.set("facility_id", facilityIdParam);
            const { data } = await api.get(`/ingestas?${q.toString()}`);
            setRows(data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error al listar");
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [estado, tipo, facilityIdParam]);

    const setFilter = (k, v) => {
        const p = new URLSearchParams(params);
        if (v && v !== "todos") p.set(k, v); else p.delete(k);
        setParams(p);
    };

    const reintentar = async (id) => {
        try {
            const { data } = await api.post(`/ingestas/${id}/reintentar`);
            toast.success(`Reintento: ${data.ingesta.estado}`);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        }
    };

    const kpis = {
        total: rows.length,
        exitoso: rows.filter((r) => r.estado === "exitoso").length,
        parcial: rows.filter((r) => r.estado === "parcial").length,
        fallido: rows.filter((r) => r.estado === "fallido").length,
    };

    return (
        <div className="p-8 lg:p-10 max-w-[1600px] space-y-6" data-testid="ingestas-page">
            <div>
                <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-dark-gold)] font-semibold mb-2 flex items-center gap-2">
                    <Waves className="h-3 w-3" /> A2 · Conexión a la fuente
                </div>
                <h1 className="font-display text-4xl tracking-tight flex items-center gap-3">
                    <ArrowUpRight className="h-8 w-8 text-[color:var(--brand-dark-gold)]" />
                    Ingestas
                    <span className="badge-mock text-xs">MOCK</span>
                </h1>
                <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                    Historial de sincronizaciones automatizadas (pull, webhook, SFTP).
                    Cada ingesta genera un corte de tape y, si hay divergencia con el corte
                    manual previo, un reporte de reconciliación descargable.
                </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="kpi-card">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Total</div>
                    <div className="font-display text-2xl mt-1">{kpis.total}</div>
                </div>
                <div className="kpi-card">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Exitosas</div>
                    <div className="font-display text-2xl mt-1 text-emerald-600">{kpis.exitoso}</div>
                </div>
                <div className="kpi-card">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Parciales</div>
                    <div className="font-display text-2xl mt-1 text-[color:var(--brand-dark-gold)]">{kpis.parcial}</div>
                </div>
                <div className="kpi-card">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Fallidas</div>
                    <div className="font-display text-2xl mt-1 text-[color:var(--brand-coral)]">{kpis.fallido}</div>
                </div>
            </div>

            <div className="flex gap-3 items-end flex-wrap">
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Estado</div>
                    <Select value={estado || "todos"} onValueChange={(v) => setFilter("estado", v)}>
                        <SelectTrigger className="w-40" data-testid="ingestas-filter-estado"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="todos">Todos</SelectItem>
                            <SelectItem value="exitoso">Exitosas</SelectItem>
                            <SelectItem value="parcial">Parciales</SelectItem>
                            <SelectItem value="fallido">Fallidas</SelectItem>
                            <SelectItem value="procesando">Procesando</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Tipo</div>
                    <Select value={tipo || "todos"} onValueChange={(v) => setFilter("tipo", v)}>
                        <SelectTrigger className="w-44" data-testid="ingestas-filter-tipo"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="todos">Todos</SelectItem>
                            <SelectItem value="api_pull">API Pull</SelectItem>
                            <SelectItem value="webhook_push">Webhook</SelectItem>
                            <SelectItem value="sftp_scheduled">SFTP</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="flex-1"></div>
                <Button variant="outline" size="sm" onClick={load} data-testid="ingestas-reload">
                    <RefreshCw className="h-3 w-3 mr-1" /> Recargar
                </Button>
            </div>

            <div className="rounded-md border border-border overflow-hidden bg-card">
                <table className="trf-table" data-testid="ingestas-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Facility</th>
                            <th>Tipo</th>
                            <th>Estado</th>
                            <th>Recibidos</th>
                            <th>Válidos</th>
                            <th>Duplicados</th>
                            <th>Rechazados</th>
                            <th>Reconc.</th>
                            <th>Duración</th>
                            <th>Ejecutado</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => {
                            const est = ESTADO_STYLE[r.estado] || ESTADO_STYLE.procesando;
                            const Icon = est.icon;
                            const recon = r.reconciliacion || {};
                            const hasRecon = recon.aplica &&
                                ((recon.nuevos_total || 0) + (recon.desaparecidos_total || 0) + (recon.cambios_total || 0)) > 0;
                            return (
                                <tr key={r.id} data-testid={`ingesta-row-${r.id}`}>
                                    <td className="hash-cell text-xs">{r.id.slice(0, 8)}…</td>
                                    <td className="text-xs">
                                        <Link to={`/v1/facilities/${r.facility_id}?tab=fuentes`} className="text-[color:var(--brand-dark-gold)] hover:underline">
                                            {r.facility_id?.slice(0, 8)}…
                                        </Link>
                                    </td>
                                    <td>
                                        <span className={`${TIPO_BADGE[r.tipo]} text-[10px]`}>{r.tipo}</span>
                                    </td>
                                    <td>
                                        <span className={est.badge}><Icon className="h-3 w-3" /> {r.estado}</span>
                                    </td>
                                    <td className="tabular text-xs">{r.registros_recibidos}</td>
                                    <td className="tabular text-xs">{r.registros_nuevos}</td>
                                    <td className="tabular text-xs">{r.registros_duplicados || 0}</td>
                                    <td className="tabular text-xs">
                                        {r.registros_rechazados > 0 ? (
                                            <span className="text-[color:var(--brand-coral)]">{r.registros_rechazados}</span>
                                        ) : "0"}
                                    </td>
                                    <td className="text-xs">
                                        {hasRecon ? (
                                            <span className="badge-gold text-[10px]" title={`+${recon.nuevos_total} −${recon.desaparecidos_total} ~${recon.cambios_total}`}>
                                                <PieChart className="h-3 w-3" />
                                                +{recon.nuevos_total || 0} −{recon.desaparecidos_total || 0} ~{recon.cambios_total || 0}
                                            </span>
                                        ) : (
                                            <span className="text-muted-foreground text-[10px]">—</span>
                                        )}
                                    </td>
                                    <td className="tabular text-xs">{r.duracion_ms}ms</td>
                                    <td className="tabular text-xs">{fmt(r.fecha_ejecucion)}</td>
                                    <td>
                                        <div className="flex gap-1">
                                            <Button
                                                size="sm" variant="ghost"
                                                onClick={() => setOpenDetail(r)}
                                                data-testid={`ingesta-detalle-${r.id}`}
                                            >
                                                <FileText className="h-3 w-3" />
                                            </Button>
                                            {hasRecon && (
                                                <a
                                                    href={`${API_BASE}/ingestas/${r.id}/reconciliacion.csv`}
                                                    target="_blank" rel="noreferrer"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        api.get(`/ingestas/${r.id}/reconciliacion.csv`, { responseType: "blob" })
                                                            .then((resp) => {
                                                                const blob = new Blob([resp.data], { type: "text/csv" });
                                                                const url = URL.createObjectURL(blob);
                                                                const a = document.createElement("a");
                                                                a.href = url;
                                                                a.download = `reconciliacion_${r.id.slice(0, 12)}.csv`;
                                                                a.click();
                                                                URL.revokeObjectURL(url);
                                                            })
                                                            .catch(() => toast.error("Descarga falló"));
                                                    }}
                                                    data-testid={`ingesta-download-recon-${r.id}`}
                                                >
                                                    <Button size="sm" variant="ghost" title="Descargar reconciliación CSV">
                                                        <Download className="h-3 w-3" />
                                                    </Button>
                                                </a>
                                            )}
                                            {canRetry && r.tipo !== "webhook_push" && r.estado !== "exitoso" && (
                                                <Button
                                                    size="sm" variant="ghost"
                                                    onClick={() => reintentar(r.id)}
                                                    data-testid={`ingesta-reintentar-${r.id}`}
                                                >
                                                    <Play className="h-3 w-3" />
                                                </Button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                        {rows.length === 0 && !loading && (
                            <tr>
                                <td colSpan={12} className="text-center py-10 text-muted-foreground">
                                    Sin ingestas registradas. Configura una fuente y ejecútala desde el detalle del facility.
                                </td>
                            </tr>
                        )}
                        {loading && (
                            <tr>
                                <td colSpan={12} className="text-center py-6">
                                    <Loader2 className="h-4 w-4 animate-spin inline" />
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {openDetail && (
                <IngestaDetalle
                    ingesta={openDetail}
                    onClose={() => setOpenDetail(null)}
                />
            )}
        </div>
    );
}


function IngestaDetalle({ ingesta, onClose }) {
    const [full, setFull] = useState(ingesta);
    useEffect(() => {
        api.get(`/ingestas/${ingesta.id}`).then(({ data }) => setFull(data)).catch(() => {});
    }, [ingesta.id]);
    const recon = full.reconciliacion || {};
    return (
        <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="sm:max-w-3xl" data-testid="ingesta-detalle-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display">
                        Ingesta {full.id?.slice(0, 12)}…
                    </DialogTitle>
                </DialogHeader>
                <div className="max-h-[70vh] overflow-y-auto space-y-4 text-sm">
                    <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-xs">
                        <div><span className="text-muted-foreground">Tipo:</span> {full.tipo}</div>
                        <div><span className="text-muted-foreground">Estado:</span> {full.estado}</div>
                        <div><span className="text-muted-foreground">Facility:</span> <span className="hash-cell">{full.facility_id?.slice(0, 12)}…</span></div>
                        <div><span className="text-muted-foreground">Fuente:</span> <span className="hash-cell">{full.fuente_id?.slice(0, 12)}…</span></div>
                        <div><span className="text-muted-foreground">Trigger:</span> {full.trigger_source}</div>
                        <div><span className="text-muted-foreground">Ejecutado:</span> {fmt(full.fecha_ejecucion)}</div>
                        <div><span className="text-muted-foreground">Duración:</span> {full.duracion_ms}ms</div>
                        <div><span className="text-muted-foreground">Corte tape:</span> <span className="hash-cell">{full.corte_tape_generado_id?.slice(0, 12) || "—"}…</span></div>
                        <div><span className="text-muted-foreground">Hash payload:</span> <span className="hash-cell">{full.hash_payload_completo?.slice(0, 16)}…</span></div>
                    </div>

                    <div className="grid grid-cols-4 gap-2 text-center">
                        <MetricPill label="Recibidos" value={full.registros_recibidos} />
                        <MetricPill label="Nuevos" value={full.registros_nuevos} />
                        <MetricPill label="Duplicados" value={full.registros_duplicados} />
                        <MetricPill label="Rechazados" value={full.registros_rechazados} coral />
                    </div>

                    {recon.aplica && (
                        <div className="border border-border rounded-md p-3 space-y-2">
                            <div className="font-medium text-sm flex items-center gap-2">
                                <PieChart className="h-4 w-4" /> Reconciliación vs corte manual
                                <span className="badge-mock text-[10px]">
                                    corte {recon.corte_manual_id?.slice(0, 8)}…
                                </span>
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-center text-xs">
                                <div>
                                    <div className="text-muted-foreground">Nuevos</div>
                                    <div className="font-display text-lg text-emerald-600">+{recon.nuevos_total}</div>
                                </div>
                                <div>
                                    <div className="text-muted-foreground">Desaparecidos</div>
                                    <div className="font-display text-lg text-[color:var(--brand-coral)]">−{recon.desaparecidos_total}</div>
                                </div>
                                <div>
                                    <div className="text-muted-foreground">Cambios</div>
                                    <div className="font-display text-lg">~{recon.cambios_total}</div>
                                </div>
                            </div>
                            {full.reconciliacion_csv_path && (
                                <Button
                                    size="sm" variant="outline"
                                    onClick={() => {
                                        api.get(`/ingestas/${full.id}/reconciliacion.csv`, { responseType: "blob" })
                                            .then((resp) => {
                                                const blob = new Blob([resp.data], { type: "text/csv" });
                                                const url = URL.createObjectURL(blob);
                                                const a = document.createElement("a");
                                                a.href = url;
                                                a.download = `reconciliacion_${full.id.slice(0, 12)}.csv`;
                                                a.click();
                                                URL.revokeObjectURL(url);
                                            });
                                    }}
                                    data-testid="ingesta-detalle-download-csv"
                                >
                                    <Download className="h-3 w-3 mr-1" /> Descargar reporte CSV
                                </Button>
                            )}
                        </div>
                    )}

                    {full.errores?.length > 0 && (
                        <div className="border border-[color:var(--brand-coral)]/40 rounded-md p-3 space-y-1">
                            <div className="font-medium text-sm">Errores ({full.errores.length})</div>
                            <ul className="text-xs text-muted-foreground space-y-0.5">
                                {full.errores.slice(0, 10).map((e, i) => (
                                    <li key={i} className="truncate">
                                        Fila {e.fila}: {e.motivo}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cerrar</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function MetricPill({ label, value, coral }) {
    return (
        <div className="border border-border rounded-md p-2">
            <div className="text-[10px] uppercase text-muted-foreground">{label}</div>
            <div className={`font-display text-xl ${coral && value > 0 ? "text-[color:var(--brand-coral)]" : ""}`}>{value ?? 0}</div>
        </div>
    );
}
